import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/components/ui/Card"
import { formatValue, formatValueFull } from "../format"
import { type Pivot, type RowKind, SERIES_SLOTS, seriesColor } from "../types"

interface Props {
  pivot: Pivot
  rows: RowKind
  currency: string | null
  title: string
  subtitle: string
  totalLabel: string
}

interface Series {
  key: string
  name: string
  color: string
}

const OTHER_KEY = "__other"

// Channel rows become stacked series (fixed color slots; rows past the last
// slot fold into "Other"). Product rows chart as one total series — twenty
// stacked product colors would be unreadable; the table carries the detail.
function buildSeries(pivot: Pivot, stacked: boolean): Series[] {
  if (!stacked) return [{ key: "total", name: "Total", color: seriesColor(0) }]
  const named = pivot.rows.slice(0, SERIES_SLOTS).map((r, i) => ({ key: r.key, name: r.label, color: seriesColor(i) }))
  if (pivot.rows.length > SERIES_SLOTS) named.push({ key: OTHER_KEY, name: "Other", color: seriesColor(SERIES_SLOTS) })
  return named
}

function buildData(pivot: Pivot, series: Series[], stacked: boolean) {
  return pivot.columns.map((col) => {
    const point: Record<string, string | number> = { label: col.label }
    if (!stacked) {
      point.total = pivot.totals[col.key].revenue
      return point
    }
    let other = 0
    pivot.rows.forEach((row, i) => {
      const v = row.cells[col.key].revenue
      if (i < SERIES_SLOTS) point[row.key] = v
      else other += v
    })
    if (series.some((s) => s.key === OTHER_KEY)) point[OTHER_KEY] = other
    return point
  })
}

export default function RevenueChart({ pivot, rows, currency, title, subtitle, totalLabel }: Props) {
  const stacked = rows === "channels" && pivot.rows.length > 1
  const series = buildSeries(pivot, stacked)
  const data = buildData(pivot, series, stacked)
  // Legend and tooltip keep the row order (org-tree position, so a channel
  // keeps its color from one period to the next), not alphabetical.
  const seriesIndex = Object.fromEntries(series.map((s, i) => [s.key, i]))
  const compact = (v: number) => formatValue(v, currency)
  const full = (v: number) => formatValueFull(v, currency)

  return (
    <Card className="px-5 pb-3 pt-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-muted-foreground">{totalLabel}</div>
          <div className="text-2xl font-semibold tabular-nums tracking-tight">{compact(pivot.grand_total.revenue)}</div>
        </div>
      </div>

      {pivot.columns.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">No days in the selected period.</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} width={64} />
            <Tooltip
              formatter={(v) => full(Number(v))}
              itemSorter={(item) => seriesIndex[String(item.dataKey)] ?? 0}
              cursor={{ fill: "var(--muted)" }}
              contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
            />
            {series.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} itemSorter={null} />
            )}
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="stack"
                fill={s.color}
                stroke="var(--card)"
                strokeWidth={stacked ? 1 : 0}
                maxBarSize={24}
                radius={i === series.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
